import { useMemo, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { FONTS } from '../../shared/theme/typography';
import { coordsOf, isMappable, mapsQueryOf, venueText, type Opportunity } from '../../shared/opportunity';
import { useAreaShapes } from '../../shared/useAreaShapes';
import { House, PIN_DARK, PIN_RED, PinMark } from './RoutePin';
import { pointFor, type TravelMode } from '../schedule/travel';
import { HEIGHT, TILE, buildFrame, project, useFrameSize } from './mapFrame';

/**
 * A small map preview in the event callout.
 *
 * Tiles are composed directly from the slippy-map grid rather than through
 * Leaflet. The map is a preview that opens Google Maps when clicked — it never
 * pans or zooms — so a mapping library would add ~150KB and an interaction
 * model we don't want. The frame and its projection are in `mapFrame`, which
 * the day's route map shares.
 *
 * The event, and the stops either side of it on the user's day: the event
 * before and the event after, or the hotel where the day starts or ends. They
 * are numbered and routed as map my day draws them, the event in the palette's
 * red and its neighbours darker. The travel times, and whether they can be
 * made, are said by the callout around it. A click opens the three in Google
 * Maps, in order.
 */

/** The route: a pale band under a maroon dash, as map my day draws it. */
const ROUTE_UNDER = '#f03d22';
const ROUTE_DASH = '#661a0f';
const HEAD_H = 20;
const TAIL_H = 6;

// ---------------------------------------------------------------------------

/** Always opens Google Maps in a new tab; see the anchor's target below. */
const Frame = styled('a')({
  display: 'block',
  position: 'relative',
  width: '100%',
  height: HEIGHT,
  borderRadius: 10,
  overflow: 'hidden',
  background: '#d9d5cd',
  border: '1px solid rgba(243,238,231,0.18)',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': { borderColor: 'rgba(243,238,231,0.45)' },
});

const Tile = styled('img')({
  position: 'absolute',
  width: TILE,
  height: TILE,
  userSelect: 'none',
  pointerEvents: 'none',
});

/**
 * The route line and the neighborhood outline, over the tiles.
 *
 * No `viewBox`: with one, the SVG's user units scale to the element, and since
 * the frame is `width: 100%` while the tiles are unscaled images at pixel
 * offsets, that drew every outline stretched sideways and sliding further off
 * the map the further it got from the left edge. Without one, a user unit is a
 * CSS pixel, which is the same space the tiles and pins are placed in.
 */
const Overlay = styled('svg')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  overflow: 'hidden',
});

/** A stop either side of this event on the user's day: an event, or the hotel. */
export interface MapNeighbour {
  /** Null for the hotel, which is drawn as a house. */
  event: Opportunity | null;
  point: [number, number];
  /** Only the neighborhood is known: a hollow pin at its centre. */
  approximate: boolean;
  /** How that journey is made, when the day has one, for the directions. */
  mode?: TravelMode;
  /** Runs at the same time as this event: the leg between them is red. */
  clash?: boolean;
}

interface Props {
  event: Opportunity;
  before?: MapNeighbour | null;
  after?: MapNeighbour | null;
  /** The frame's height, as any CSS length. */
  height?: string;
  /** Between the map and its attribution: the callout's travel lines. */
  below?: ReactNode;
}

const GOOGLE_MODE: Record<TravelMode, string> = {
  walk: 'walking',
  bike: 'bicycling',
  transit: 'transit',
  ferry: 'transit',
};

/** What Google Maps is asked for: an address, else the neighborhood, else the point. */
const placeQuery = (event: Opportunity | null, point: [number, number] | null): string | null =>
  (event && mapsQueryOf(event)) ??
  (point
    ? point.join(',')
    : event?.neighborhood
      ? `${event.neighborhood.replace(' - ', ', ')}, New York`
      : null);

export default function EventMap({ event, before = null, after = null, height, below }: Props) {
  const areaShapes = useAreaShapes();
  const [frameRef, size] = useFrameSize();

  /**
   * An event with a neighborhood but no street address gets the neighborhood
   * outlined instead of a pin — we know roughly where it is, and a pin would
   * claim a precision the data doesn't have.
   */
  const coords = coordsOf(event);
  const areaShape = !coords && event.neighborhood ? (areaShapes[event.neighborhood] ?? null) : null;
  const outlineMode = !coords && Boolean(areaShape);

  /** For an outline, frame the ring's own corners so the whole shape fits. */
  const areaPoints = useMemo<[number, number][]>(() => {
    if (!areaShape) return [];
    const lats = areaShape.ring.map((c) => c[1]);
    const lngs = areaShape.ring.map((c) => c[0]);
    return [
      [Math.min(...lats), Math.min(...lngs)],
      [Math.max(...lats), Math.max(...lngs)],
    ];
  }, [areaShape]);

  const beforePoint = before?.point;
  const afterPoint = after?.point;
  const frame = useMemo(() => {
    const own = coords ? [coords] : areaPoints;
    if (!own.length) return null;
    const points = [...(beforePoint ? [beforePoint] : []), ...own, ...(afterPoint ? [afterPoint] : [])];
    // An outline alone wants more room around it than pins do.
    const alone = !beforePoint && !afterPoint && outlineMode;
    return buildFrame(points, size, alone ? 84 : undefined, alone ? 40 : undefined).frame;
    // `coords` is a fresh array each render; its two numbers are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords?.[0], coords?.[1], areaPoints, beforePoint, afterPoint, outlineMode, size]);

  // Neither a coordinate nor a known neighborhood means no map at all.
  if (!frame) return null;

  const place = ([lat, lng]: [number, number]) => {
    const p = project(lat, lng, frame.zoom);
    return { left: p.x - frame.originX, top: p.y - frame.originY };
  };

  /** Project the ring into the frame, for the outline path. */
  const areaPath =
    outlineMode && areaShape
      ? areaShape.ring
          .map(([lng, lat], i) => {
            const p = project(lat, lng, frame.zoom);
            return `${i === 0 ? 'M' : 'L'}${(p.x - frame.originX).toFixed(1)},${(p.y - frame.originY).toFixed(1)}`;
          })
          .join(' ') + ' Z'
      : null;

  /**
   * The stops in the day's order, numbered as map my day numbers them: from 1
   * on the first event, the hotel a house. The event's own is where its route
   * meets it: its address, or its neighborhood's centre when outlined.
   */
  const ownPoint = coords ?? pointFor(event);
  let n = 0;
  const stops = [
    before && { key: 'before', at: place(before.point), home: !before.event, hollow: before.approximate, colour: PIN_DARK },
    ownPoint && {
      key: 'this',
      at: place(ownPoint),
      home: false,
      hollow: !coords,
      colour: PIN_RED,
      // An outline alone is the event's mark; with a route through it, it gets its number too.
      hidden: outlineMode && !before && !after,
    },
    after && { key: 'after', at: place(after.point), home: !after.event, hollow: after.approximate, colour: PIN_DARK },
  ]
    .filter((s): s is NonNullable<typeof s> => Boolean(s))
    .map((s) => ({ ...s, label: s.home ? <House /> : String(++n) }));

  // A pin whose head would cover another's steps sideways until both read.
  const drawn: { l: number; r: number; t: number }[] = [];
  const pins = stops.map((s) => {
    const half = 10;
    const t = s.at.top - TAIL_H - HEAD_H;
    let left = s.at.left;
    for (let guard = 0; guard < 6; guard += 1) {
      const hit = drawn.find((d) => left - half < d.r + 2 && left + half > d.l - 2 && Math.abs(t - d.t) < HEAD_H);
      if (!hit) break;
      left = left >= (hit.l + hit.r) / 2 ? hit.r + 2 + half : hit.l - 2 - half;
    }
    if (!('hidden' in s && s.hidden)) drawn.push({ l: left - half, r: left + half, t });
    return { ...s, left };
  });

  const legs = [
    before && ownPoint && { key: 'in', a: place(before.point), b: place(ownPoint), clash: Boolean(before.clash) },
    after && ownPoint && { key: 'out', a: place(ownPoint), b: place(after.point), clash: Boolean(after.clash) },
  ].filter((l): l is NonNullable<typeof l> => Boolean(l));

  // Google Maps: the stops in order, or the event alone.
  const own = placeQuery(event, coords);
  const route = [
    before && placeQuery(before.event, before.approximate ? null : before.point),
    own,
    after && placeQuery(after.event, after.approximate ? null : after.point),
  ].filter((q): q is string => Boolean(q));
  const modes = [before?.mode, after?.mode].filter((m): m is TravelMode => Boolean(m));
  // Google takes no stops in between on transit: a mode only when all legs share one that allows them.
  const mode =
    route.length === 2 && modes[0]
      ? GOOGLE_MODE[modes[0]]
      : modes.length && modes.every((m) => m === modes[0] && m !== 'transit' && m !== 'ferry')
        ? GOOGLE_MODE[modes[0]]
        : null;
  const href =
    route.length >= 2
      ? 'https://www.google.com/maps/dir/?api=1' +
        `&origin=${encodeURIComponent(route[0])}&destination=${encodeURIComponent(route[route.length - 1])}` +
        (route.length > 2 ? `&waypoints=${encodeURIComponent(route.slice(1, -1).join('|'))}` : '') +
        (mode ? `&travelmode=${mode}` : '')
      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(own ?? '')}`;
  const where = isMappable(event) ? venueText(event) : (event.neighborhood ?? venueText(event));

  return (
    <Box>
      <Frame
        ref={frameRef}
        href={href}
        style={height ? { height } : undefined}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={
          route.length >= 2
            ? `Open directions through ${where} in Google Maps: from the stop before it to the one after`
            : `Open ${where} in Google Maps`
        }
      >
        {frame.tiles.map((t) => (
          <Tile key={t.key} src={t.url} alt="" loading="lazy" style={{ left: t.left, top: t.top }} />
        ))}

        {areaPath && (
          <Overlay aria-hidden="true">
            <path
              d={areaPath}
              fill={PIN_RED}
              fillOpacity={0.12}
              stroke={PIN_RED}
              strokeWidth={2.5}
              strokeLinejoin="round"
            />
          </Overlay>
        )}

        {legs.length > 0 && (
          <Overlay aria-hidden="true">
            {legs.map((l) => (
              <g key={l.key}>
                <line x1={l.a.left} y1={l.a.top} x2={l.b.left} y2={l.b.top} stroke={ROUTE_UNDER} strokeOpacity={0.2} strokeWidth={6} strokeLinecap="round" />
                <line
                  x1={l.a.left}
                  y1={l.a.top}
                  x2={l.b.left}
                  y2={l.b.top}
                  stroke={l.clash ? PIN_RED : ROUTE_DASH}
                  strokeWidth={2.5}
                  strokeDasharray="6 5"
                  strokeLinecap="round"
                />
              </g>
            ))}
          </Overlay>
        )}

        {pins.map((p) =>
          'hidden' in p && p.hidden ? null : (
            <Box
              key={p.key}
              aria-hidden="true"
              sx={{
                position: 'absolute',
                left: p.left,
                top: p.at.top,
                transform: 'translate(-50%, -100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
                // The event itself above the stops either side of it.
                zIndex: p.key === 'this' ? 2 : 1,
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
              }}
            >
              <PinMark label={p.label} hollow={p.hollow} colour={p.colour} />
            </Box>
          ),
        )}
      </Frame>

      {below}

      {/* Stadia's terms ask for the credit wherever the tiles are shown. */}
      <Typography sx={{ fontSize: 10.5, opacity: 0.42, mt: 0.7, fontFamily: FONTS.body }}>
        {areaShape
          ? 'No street address published — showing the neighborhood. '
          : 'Tap the map for directions. '}
        Tiles by Stamen / Stadia Maps, data © OpenStreetMap.
      </Typography>
    </Box>
  );
}
